import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Times from '~/bootcamp/Activities/components/Times'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { hocOnEnterPress, parseWholeNumber } from '~/bootcamp/Activities/utils/activities.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { intDiff, getMultiBy, spaceEveryThousand } from './constants'
import { NumHistory } from './types'

export default function MultiBy10({
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  difficultyLevel,
  levelId,
  onNextQuestionClick,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const [answer, setAnswer] = useState('')
  const [correctAnswer, setCorrectAnswer] = useState(0)
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [taskHistory, setTaskHistory] = useState<NumHistory[]>([])
  const [multiBy, setMultiBy] = useState(levelId ? getMultiBy(parseInt(levelId) - 1) : 10)
  const historyNumbers = useMemo(() => taskHistory.map(({ num }) => num), [taskHistory])
  const difficulty = intDiff[difficultyLevel - (levelId === '4' ? 0 : 1)]
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const num = useMemo(() => {
    if (difficultyLevel && levelId) {
      return generateInt(difficulty.MIN, difficulty.MAX, multiBy, levelId, setMultiBy, setCorrectAnswer, historyNumbers)
    }
  }, [historyNumbers, difficultyLevel, multiBy, levelId, difficulty.MIN, difficulty.MAX])

  useEffect(() => {
    setAnswer('')
  }, [num])

  useEmitInstance(onInstanceChange, num && multiBy ? `${num} \\cdot ${multiBy} = \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = correctAnswer === parseWholeNumber(answer)
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    onAnswerStateChange(null)
    setAnswer('')

    if (num) {
      setTaskHistory((prevState) => {
        return [{ num }, ...prevState].slice(0, 3)
      })
    }
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper className="pt-5 md:py-5">
      <MultiplicationActivityWrapper>
        <div>
          <span>{num ? spaceEveryThousand(num) : ''}</span>
          <Times />
          <span>{multiBy ? spaceEveryThousand(multiBy) : ''}</span>
        </div>
        <div>
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertProduct')}
            autoFocus={autofocus}
            isError={answerState === false}
            isWide
            key={inputKey}
            maxDigits={20}
            onChange={(value) => {
              setAnswer(value)
              onAnswerStateChange(null)
            }}
            onEnter={onEnterPress}
            ref={inputRef}
            type="text"
            value={answer}
          />
        </div>
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateInt(
  min: number,
  max: number,
  multiBy: number,
  levelId: string,
  setMultiBy: Dispatch<SetStateAction<number>>,
  setCorrectAnswer: Dispatch<SetStateAction<number>>,
  historyNumbers: number[],
): number {
  const num = Math.floor(Math.random() * (max - min + 1) + min)

  setMultiBy(getMultiBy(parseInt(levelId) - 1))

  if (historyNumbers.includes(num)) {
    return generateInt(min, max, multiBy, levelId, setMultiBy, setCorrectAnswer, historyNumbers)
  }

  setCorrectAnswer(num * multiBy)
  return num
}
